import { ReactNode } from "react";
import { Citation, TaskLabels } from "../shared/types";
import { z } from "zod";

export type ChatEventStatus = "idle" | "pending" | "streaming" | "complete" | "error" | "cancelled";

export type ChatEvent =
  | { type: "session_created"; sessionId: string }
  | { type: "status_change"; status: ChatEventStatus; sessionId: string };

export interface AgentAction {
  id?: string;
  type: "info" | "analysis";
  content: string;
  timestamp: number;
  artifacts?: Artifact[];
}

export enum LoadingState {
  LOADING = "LOADING",
  LOADED = "LOADED",
  ERROR = "ERROR",
  UNINITIALIZED = "UNINITIALIZED",
  CANCELLED = "cancelled",
}

export interface AssistantMeta {
  avatar: string;
  name: string;
}

export interface Feedback {
  rating?: "up" | "down";
  comment?: string;
}

export type ContextOption =
  | {
      label: string;
      key: string;
      type: "submenu";
      options: { id: string; label: string }[];
      icon?: ReactNode;
    }
  | {
      label: string;
      key: string;
      type: "trigger";
      handler?: () => void;
      icon?: ReactNode;
    };

export interface ChatSession {
  id: string;
  messages: ChatMessage[];
  context?: {
    files?: File[];
    [x: string]: unknown;
  };
  selectedModel?: string | null;
  datamates?: string[];
  status?: SessionStatusEnum;
}

export interface FileUploadProps {
  allowedFiles: string; // e.g. ".txt,.json,.md"
  contextFieldKey: string;
}

export interface ChatbotUrls {
  origin?: string;
  askPath?: string;
  proceedPath?: string;
  frontendUrl?: string;
  followupPath?: string;
  entityDetectionUrl?: string;
  feedbackPath?: string;
}

export interface Datamate {
  id: string;
  label: string;
}

export enum EntityType {
  SNOWFLAKE_ACCOUNT = "snowflake_account",
  SNOWFLAKE_WAREHOUSE = "snowflake_warehouse",
  SNOWFLAKE_DATABASE = "snowflake_database",
  SNOWFLAKE_TABLE = "snowflake_table",
  SNOWFLAKE_USER = "snowflake_user",
  SNOWFLAKE_ROLE = "snowflake_role",
  WAREHOUSE = "warehouse",
  TABLE = "table",
  QUERY = "query",
  USER = "user",
  WORKLOAD = "workload",
  // Databricks-only entity types (AI-6339). The shared values above
  // (warehouse/query) double as Databricks types when paired with a
  // databricks provider context — the FE picks the URL builder per provider.
  CLUSTER = "cluster",
  JOB = "job",
  // Tableau citation entity types. The backend `TableauCitationResolver`
  // rewrites the model-emitted LUID / email into a URL-encoded full
  // `tableau_*:N://site.luid` rk before the FE sees it, so the entityId
  // passed to the URL builder is already encoded for path use.
  TABLEAU_DASHBOARD = "tableau_dashboard",
  TABLEAU_USER = "tableau_user",
  TABLEAU_DATASOURCE = "tableau_datasource",
  TABLEAU_DASHBOARD_GROUP = "tableau_dashboard_group",
  TABLEAU_JOB = "tableau_job",
  UNKNOWN = "unknown",
}

export interface DetectedEntity {
  name: string;
  types_found: EntityType[];
  selected_type: EntityType;
}

export interface EntityDetectionRequest {
  text: string;
  detected_entities?: string[];
}

export interface EntityDetectionResponse {
  entities: Pick<DetectedEntity, "name" | "types_found">[];
  original_text: string;
}

export interface ChatbotProps {
  helloMessage?: ReactNode;
  assistantMeta?: AssistantMeta;
  context?: Record<string, unknown>;
  fileUploadProps?: FileUploadProps;
  contextOptions?: ContextOption[];
  placeholder?: string;
  autoFocus?: boolean;
  classNames?: {
    chatbot?: string;
    chatMessages?: string;
    messageItem?: string;
    questionForm?: string;
    chatbotScrollingContent?: string;
  };
  initialMessage?: string;
  submitInitialMessageOnMount?: boolean;
  questionFormLeftActions?: ReactNode[];
  refetchSession?: () => Promise<unknown>;
  chatboxStartPosition?: "middle" | "bottom";
  helloMessageFooter?: ReactNode;
  handleSavePrompt?: (message: ChatMessage) => void;
  showSavePromptButton?: boolean;
}

export interface ChatbotProviderProps extends ChatbotProps {
  taskLabel?: keyof typeof TaskLabels;
  initialValidation?: ChatState["initialValidation"];
  onError?: (error: unknown) => void;
  onEvent?: (event: ChatEvent) => void;
  urls?: ChatbotUrls;
  /**
   * AI-6339: tenant provider context. Drives per-provider citation URL routing
   * inside the markdown renderer. Defaults to `"snowflake"` when omitted.
   */
  provider?: "snowflake" | "databricks";
  disableContext?: boolean;
  requestParams?: RequestInit;
  components?: {
    feedback?: ReactNode;
    questionFormButtons?: (
      userPrompt: string,
      onAccept: (improvedPrompt: string) => void
    ) => ReactNode;
    messageActions?: (ctx: {
      sessionId?: string;
      messageId: string;
      isLastMessage: boolean;
    }) => ReactNode;
    // Opt-in REPLACEMENT for the default ShareMessage button (which opens
    // EmailDialog). When provided, every per-message Share button in this
    // chatbot surface renders the consumer's component instead. Studio
    // uses this to swap in StudioShareButton, which opens the full
    // ShareDialog (Link / Slack / Email tabs) — see AI-6343.
    shareButton?: (ctx: { sessionId?: string }) => ReactNode;
  };
  contextOptions?: ContextOption[];
  isMarkdownResponse?: boolean;
  models?: { label: string; value: string }[];
  placeholder?: string;
  sessions?: Record<string, ChatSession>;
  currentSessionId?: string;
  selectedDatamateId?: string;
  handleSavePrompt?: (message: ChatMessage) => void;
}

export type InteractionType = "text" | "select" | "multiSelect" | "confirm";

export interface InteractionChoice {
  id: string;
  label: string;
  value: string;
}

export interface InteractionRequest {
  type: InteractionType;
  id: string;
  prompt: string;
  required?: boolean;
  choices?: InteractionChoice[];
  validation?: {
    pattern?: string;
    message?: string;
  };
}

export interface UploadedFile {
  filename: string;
  content: string; // base64 encoded content
  content_type: string;
  size: number;
}

export interface MessageAttachment {
  id: string;
  attachment_name: string;
  file_type: string | null;
  s3_url: string | null;
  extracted_content: string | null;
  order: number;
  attachment_type: string; // 'file', 'datamate', 'knowledge_base'
  attachment_reference_id: string | null;
  file_size: number | null;
  created_on: string; // ISO date string
}

export interface ChatMessage {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: number;
  actions?: AgentAction[];
  interaction?: InteractionRequest;
  artifacts?: Artifact[];
  heading: string;
  citations?: Citation[];
  todos?: TodoItem[];
  attachments?: MessageAttachment[];
  confidence_str?: string;
}

export interface Artifact {
  content: string;
  entity: string;
  name: string;
  type: string;
}

export interface ChatResponse {
  content: string;
  actions?: AgentAction[];
  interaction?: InteractionRequest;
  role: "assistant" | "user";
  timestamp: number;
  artifacts?: Artifact[];
  heading: string;
  todos?: TodoItem[];
}

// Session Status Enum
export const sessionStatusSchema = z.enum(["failed", "completed", "pending", "cancelled"]);
export type SessionStatusEnum = z.infer<typeof sessionStatusSchema>;

export interface ChatState {
  sessions: Record<string, ChatSession>;
  currentSessionId: string | null;
  loadingState: LoadingState;
  error: string | null;
  abortController: AbortController | null;
  currentActions: AgentAction[];
  currentTodos: TodoItem[];
  taskLabel?: keyof typeof TaskLabels;
  initialValidation?: (data: { context: ChatSession["context"] }) => Promise<void>;
  urls?: ChatbotUrls;
  /**
   * AI-6339: provider context plumbed through useChatContext() to the
   * markdown renderer for per-provider citation URL routing.
   */
  provider?: "snowflake" | "databricks";
  disableContext?: boolean;
  requestParams?: RequestInit;
  isMarkdownResponse?: boolean;
  models?: { label: string; value: string }[];
  classNames?: {
    chatbot?: string;
    chatMessages?: string;
    messageItem?: string;
    questionForm?: string;
    chatbotScrollingContent?: string;
  };
  components?: {
    feedback?: ReactNode;
    questionFormButtons?: (
      userPrompt: string,
      onAccept: (improvedPrompt: string) => void
    ) => ReactNode;
    messageActions?: (ctx: {
      sessionId?: string;
      messageId: string;
      isLastMessage: boolean;
    }) => ReactNode;
    shareButton?: (ctx: { sessionId?: string }) => ReactNode;
  };
}

export const todoItemSchema = z.object({
  id: z.string(),
  content: z.string(),
  status: z.enum(["pending", "in_progress", "completed"]),
  priority: z.enum(["high", "medium", "low"]),
});

export type TodoItem = z.infer<typeof todoItemSchema>;

export interface ToolUsageData {
  tool: string;
  id: string;
}

export interface ProgressUpdate {
  timestamp: string;
  todos: TodoItem[];
}

export interface FinalResponseData {
  tool_usage?: ToolUsageData[];
  turns?: number;
  tools_used?: number;
  progress_updates?: ProgressUpdate[];
  total_progress_updates?: number;
}

export const agentStreamResponseSchema = z.object({
  id: z.coerce.string().optional(),
  type: z.enum([
    "info",
    "agent_outcome",
    "require_user_action",
    "analysis",
    "error",
    "citations",
    "text",
    "tool_use",
    "final_response",
    "complete",
    "stream_complete", // Backend uses this as end marker
    "custom",
  ]),
  heading: z.string().optional(),
  content: z.string().optional(),
  tool: z.string().optional(),
  todos: z.array(todoItemSchema).optional(),
  response: z.string().optional(),
  data: z
    .object({
      tool_usage: z
        .array(
          z.object({
            tool: z.string(),
            id: z.string(),
          })
        )
        .optional(),
    })
    .optional(),
  interaction: z
    .object({
      type: z.enum(["text", "multiSelect", "confirm", "select"]),
      id: z.string(),
      prompt: z.string(),
      required: z.boolean().optional(),
      choices: z
        .array(
          z.object({
            id: z.string(),
            label: z.string(),
            value: z.string(),
          })
        )
        .optional(),
    })
    .optional(),
  artifacts: z
    .array(
      z.object({
        content: z.string(),
        entity: z.string(),
        name: z.string(),
        type: z.string(),
      })
    )
    .optional(),
  citations: z
    .array(
      z.object({
        content: z.string(),
        entity: z.string(),
        name: z.string(),
        type: z.string(),
      })
    )
    .optional(),
  confidence_str: z.string().optional(),
});

export interface AgentStreamResponse {
  id?: string;
  type:
    | "info"
    | "agent_outcome"
    | "require_user_action"
    | "analysis"
    | "error"
    | "citations"
    | "text"
    | "tool_use"
    | "final_response"
    | "complete"
    | "stream_complete"; // Backend uses this as end marker
  heading?: string;
  content?: string;
  tool?: string;
  todos?: TodoItem[];
  response?: string;
  data?: FinalResponseData;
  interaction?: {
    type: "text" | "multiSelect" | "confirm" | "select";
    id: string;
    prompt: string;
    required?: boolean;
    choices?: Array<{
      id: string;
      label: string;
      value: string;
    }>;
  };
  artifacts?: Artifact[];
  citations?: Citation[];
  confidence_str?: string;
}
